import { getKeycloak } from "../keycloak";

/**
 * How many seconds of validity every upload-flow request asks Keycloak for before it goes
 * out (AKL-891). `updateToken` resolves at once when the token is still good for this long
 * and refreshes it otherwise, so a request never leaves with a token about to expire —
 * or, after a long sleep, one that expired hours ago while the refresh timer slept too.
 */
export const UPLOAD_TOKEN_MIN_VALIDITY_SECONDS = 60;

/**
 * Why a refresh failed.
 * - `sessionEnded`: Keycloak refused the refresh token (HTTP 400) — the session is over.
 *   `keycloak-js` has already run `clearToken()`, which fires `onAuthLogout` and, under
 *   `onLoad: "login-required"`, starts a whole-document navigation to the sign-in page.
 *   Nothing sent after this point can succeed and the page is about to go.
 * - `unavailable`: the refresh request got no answer (status 0 — the network is not back
 *   yet). The token is left as it was; asking again once online is the right thing to do.
 */
export type TokenRefreshFailure = "sessionEnded" | "unavailable";

/** The rejection `refreshTokenBeforeRequest` throws; nothing Keycloak-shaped escapes it. */
export class TokenRefreshError extends Error {
  readonly kind: TokenRefreshFailure;

  constructor(kind: TokenRefreshFailure) {
    super(
      kind === "sessionEnded"
        ? "The Keycloak session has ended"
        : "Keycloak could not be reached to refresh the token",
    );
    this.name = "TokenRefreshError";
    this.kind = kind;
  }
}

/**
 * Whether `error` is a `TokenRefreshError`. Checked by `name` as well as `instanceof`, the
 * way the upload manager recognises the SDK's `RestError`: a transpiled `extends Error` is
 * not guaranteed to keep the prototype chain `instanceof` needs.
 * @param error whatever was caught
 */
export const isTokenRefreshError = (
  error: unknown,
): error is TokenRefreshError =>
  error instanceof TokenRefreshError ||
  (typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "TokenRefreshError");

/**
 * Refreshes the Keycloak token if it has less than `UPLOAD_TOKEN_MIN_VALIDITY_SECONDS` left,
 * so the request that follows carries a live one (AKL-891). Called first in every
 * upload-flow API function — the SAS fetch, `start_upload`, `end_upload` and each retry of
 * the final `end_upload`. On a refresh, `@react-keycloak/core` writes the new token to
 * `localStorage.token` from its `onTokens` hook before the promise resolves, which is where
 * the axios request interceptor reads it.
 *
 * `keycloak-js` 23.0.7 rejects `updateToken` with a bare `true` whatever went wrong, so the
 * rejection itself says nothing. What tells the two failures apart is the token: a 400 runs
 * `clearToken()` first and the token is gone (`sessionEnded`); a request that never got an
 * answer leaves it in place (`unavailable`). `authHeader()` is deliberately not involved —
 * it logs the user out when it finds no token, which is a decision for the caller.
 *
 * @throws TokenRefreshError with the kind of failure
 */
export async function refreshTokenBeforeRequest(): Promise<void> {
  const keycloak = getKeycloak();
  try {
    await keycloak.updateToken(UPLOAD_TOKEN_MIN_VALIDITY_SECONDS);
  } catch (error) {
    throw new TokenRefreshError(
      keycloak.token ? "unavailable" : "sessionEnded",
    );
  }
}
